import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { rankingsAPI } from '../lib/api';
import { getInitials } from '../lib/utils';
import { PowerLevelBadge, PowerLevelInline } from '../components/PowerLevelBadge';
import { StreakFire } from '../components/TrophyCase';
import { getPowerTier, POWER_TIERS } from '../lib/powerLevel';
import { Trophy, Award, Medal, Zap, Target, Shield } from 'lucide-react';

export default function Rankings() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState([]);
  const [awards, setAwards] = useState([]);
  const [activeTab, setActiveTab] = useState('overall');

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const [rankingsRes, awardsRes] = await Promise.all([
          rankingsAPI.getAll(activeTab),
          rankingsAPI.getAwards(),
        ]);
        setRankings(rankingsRes.data || []);
        setAwards(awardsRes.data || []);
      } catch (error) {
        console.error('Error fetching rankings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRankings();
  }, [activeTab]);

  const getAwardIcon = (awardName) => {
    if (awardName.includes('Smash')) return <Zap className="h-5 w-5" />;
    if (awardName.includes('Defense')) return <Shield className="h-5 w-5" />;
    if (awardName.includes('Streak')) return <Target className="h-5 w-5" />;
    if (awardName.includes('Veteran')) return <Medal className="h-5 w-5" />;
    return <Award className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full" />
          <div className="space-y-4">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20" />)}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="rankings-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">{t('rankings.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('rankings.subtitle')}</p>
        </div>

        {/* Tier Legend */}
        <div className="flex flex-wrap gap-2">
          {POWER_TIERS.map(tier => (
            <div key={tier.name} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/50 text-xs">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tier.color, boxShadow: `0 0 4px ${tier.color}60` }} />
              <span className="font-heading font-bold uppercase" style={{ color: tier.color }}>{t(`powerLevel.${tier.name}`)}</span>
              <span className="text-muted-foreground">{tier.min}+</span>
            </div>
          ))}
        </div>

        {/* Awards Section */}
        {awards.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {awards.map((award) => (
              <Card key={`${award.award}-${award.user_id || award.name}`} className="card-neon">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-lg bg-accent/20 text-accent">{getAwardIcon(award.award)}</div>
                    <div className="min-w-0">
                      <h3 className="font-heading font-bold text-sm truncate">{award.award}</h3>
                      <p className="text-[10px] text-muted-foreground truncate">{award.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px]">{getInitials(award.name)}</AvatarFallback></Avatar>
                    <div>
                      <p className="font-medium text-xs">{award.name}</p>
                      <p className="text-[10px] text-accent font-bold">{award.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Rankings Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Trophy className="h-5 w-5 text-accent" />{t('rankings.leaderboard')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="overall">{t('rankings.overall')}</TabsTrigger>
                <TabsTrigger value="singles">{t('rankings.singles')}</TabsTrigger>
                <TabsTrigger value="doubles">{t('rankings.doubles')}</TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab}>
                {rankings.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Trophy className="h-12 w-12 mx-auto mb-4 opacity-40" />
                    <p>{t('rankings.noRankings')}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {rankings.map(player => {
                      const tier = getPowerTier(player.rating || 1200);
                      return (
                        <div
                          key={player.player_id}
                          className={`flex items-center justify-between p-3 sm:p-4 rounded-lg transition-colors ${
                            player.rank <= 3 ? 'bg-muted' : 'bg-muted/40'
                          }`}
                          style={player.rank <= 3 ? { borderLeft: `3px solid ${tier.color}` } : {}}
                        >
                          <div className="flex items-center gap-3">
                            {/* Rank */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                              player.rank === 1 ? 'bg-yellow-500 text-black' : player.rank === 2 ? 'bg-gray-400 text-black' : player.rank === 3 ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}>{player.rank}</div>

                            <Avatar className="h-9 w-9">
                              <AvatarImage src={player.picture} />
                              <AvatarFallback className="text-xs">{getInitials(player.name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <p className="font-heading font-bold text-sm">{player.name}</p>
                                <StreakFire currentStreak={player.best_streak || 0} />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {player.matches_played} {t('rankings.matches')} - {player.win_rate}% {t('rankings.winRateLabel')}
                              </p>
                            </div>
                          </div>

                          <div className="text-right">
                            <PowerLevelInline elo={player.rating || 1200} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
